/**
 * NewStores - Nuevas Tiendas section for marketplace landing
 * White container on gray canvas design
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Store, Sparkles, Truck, ChevronRight } from 'lucide-react';
import { getNewStores } from '../../services/api';
import { VerifiedStoreName } from './VerifiedBadge';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
};

const NewStores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getNewStores();
        setStores(res.data);
      } catch (e) {
        console.error('Error loading new stores:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-5 h-5" style={{ color: COLORS.orange }} />
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">Nuevas Tiendas</h2>
          </div>
          <div className="flex gap-3 overflow-hidden">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex-shrink-0 w-56 h-28 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (stores.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 mt-4">
      <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: COLORS.orange }} />
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">Nuevas Tiendas</h2>
          </div>
          <Link to="/tiendas?sort=newest" className="flex items-center gap-1 text-sm font-medium hover:underline" style={{ color: COLORS.navyBlue }}>
            Ver todas <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {stores.map(store => (
            <Link
              key={store.business_id}
              to={`/tienda/${store.business_id}`}
              className="flex-shrink-0 w-56 bg-gray-50 rounded-lg hover:shadow-md hover:bg-white transition-all p-4 relative"
            >
              {/* "Nuevo" badge */}
              <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-bold rounded-md text-white" style={{ backgroundColor: COLORS.orange }}>
                Nuevo
              </span>

              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-white overflow-hidden">
                  {store.logo_url ? (
                    <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <Store className="w-6 h-6" style={{ color: COLORS.navyBlue }} />
                  )}
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <VerifiedStoreName
                    name={store.name}
                    verified={store.is_verified}
                    size="sm"
                    className="max-w-full overflow-hidden"
                    nameClassName="font-semibold truncate text-sm block"
                  />
                  {store.category && (
                    <p className="text-xs text-slate-500 truncate">{store.category}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                {store.delivery_enabled && (
                  <span className="flex items-center gap-0.5" style={{ color: COLORS.navyBlue }}>
                    <Truck className="w-3 h-3" /> Envio
                  </span>
                )}
                {store.pickup_enabled && (
                  <span className="flex items-center gap-0.5" style={{ color: COLORS.orange }}>
                    <Store className="w-3 h-3" /> Recoger
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewStores;
